import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Icon, Button } from '@/components/atoms';
import {
  useSettingsList,
  useUpsertSettingMutation,
  useUploadAttachmentMutation,
  useAttachmentDownloadUrl,
} from '@/hooks/useErpApi';
import { erpClient } from '@/services/clients';
import { settingsCache } from '@/services/settingsCache';
import {
  addressSchema,
  bankDetailsSchema,
  gstinSchema,
  panSchema,
  cinSchema,
  tanSchema,
  parseAddress,
  parseBankDetails,
  stringifyAddress,
  stringifyBankDetails,
  type Address,
  type BankDetails,
} from '@/schemas/settings';

export default function FPOSettingsPage() {
  const upsertSetting = useUpsertSettingMutation();
  const uploadLogo = useUploadAttachmentMutation();

  // Load all settings at once
  const { data: settingsData, isLoading: isLoadingSettings, refetch: refetchSettings } = useSettingsList();
  
  const isCached = useMemo(() => {
    return (settingsData as any)?.message === 'Retrieved from cache';
  }, [settingsData]);

  const forceRefetchSettings = useCallback(async () => {
    await settingsCache.invalidateCache();
    await refetchSettings();
  }, [refetchSettings]);

  // Extract individual settings from the list
  const settings = useMemo(() => {
    if (!settingsData) return [];
    if (Array.isArray(settingsData)) {
      return settingsData;
    }
    if ((settingsData as any)?.data && Array.isArray((settingsData as any).data)) {
      return (settingsData as any).data;
    }
    return [];
  }, [settingsData]);
  
  const getSetting = useCallback((key: string): string | null => {
    const setting = settings.find((s: any) => s.key === key);
    return setting?.value || null;
  }, [settings]);

  // Form states
  const [fpoName, setFpoName] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [cin, setCin] = useState('');
  const [tan, setTan] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [currentLogoAttachmentId, setCurrentLogoAttachmentId] = useState<string | null>(null);

  // Get logo URL if logo ID exists (only fetch if we don't already have a preview from upload)
  const logoAttachmentId = getSetting('fpo_logo_attachment_id');
  const attachmentIdToFetch = currentLogoAttachmentId || logoAttachmentId;
  const { data: logoUrlData, error: logoUrlError } = useAttachmentDownloadUrl(attachmentIdToFetch || '', {
    enabled: !!attachmentIdToFetch && !logoPreview,
    queryKey: `logo-url-${attachmentIdToFetch}`,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Address states
  const [branchAddress, setBranchAddress] = useState<Address>({
    type: 'branch',
    line1: '',
    line2: '',
    city: '',
    district: '',
    state: '',
    pincode: '',
    country: 'India',
  });

  const [registeredAddress, setRegisteredAddress] = useState<Address>({
    type: 'registered',
    line1: '',
    line2: '',
    city: '',
    district: '',
    state: '',
    pincode: '',
    country: 'India',
  });

  // Bank details state
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    branch_name: '',
    account_type: '',
  });

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<Record<string, 'idle' | 'saving' | 'success' | 'error'>>({
    fpoName: 'idle',
    gstin: 'idle',
    pan: 'idle',
    cin: 'idle',
    tan: 'idle',
    logo: 'idle',
    branchAddress: 'idle',
    registeredAddress: 'idle',
    bankDetails: 'idle',
  });

  // Load existing data into form when settings are loaded
  useEffect(() => {
    if (settings.length === 0) return;

    const fpoNameValue = getSetting('fpo_name');
    if (fpoNameValue && fpoNameValue !== fpoName) setFpoName(fpoNameValue);

    const gstinValue = getSetting('fpo_gstin');
    if (gstinValue && gstinValue !== gstin) setGstin(gstinValue);

    const panValue = getSetting('fpo_pan');
    if (panValue && panValue !== pan) setPan(panValue);

    const cinValue = getSetting('fpo_cin');
    if (cinValue && cinValue !== cin) setCin(cinValue);

    const tanValue = getSetting('fpo_tan');
    if (tanValue && tanValue !== tan) setTan(tanValue);

    const logoIdFromSettings = getSetting('fpo_logo_attachment_id');
    if (logoIdFromSettings && !currentLogoAttachmentId) setCurrentLogoAttachmentId(logoIdFromSettings);

    const branchAddressValue = getSetting('fpo_branch_address');
    if (branchAddressValue) {
      const parsed = parseAddress(branchAddressValue);
      if (parsed) setBranchAddress(parsed);
    }

    const registeredAddressValue = getSetting('fpo_registered_address');
    if (registeredAddressValue) {
      const parsed = parseAddress(registeredAddressValue);
      if (parsed) setRegisteredAddress(parsed);
    }

    const bankDetailsValue = getSetting('fpo_bank_details');
    if (bankDetailsValue) {
      const parsed = parseBankDetails(bankDetailsValue);
      if (parsed) setBankDetails(parsed);
    }
  }, [settings, getSetting, fpoName, gstin, pan, cin, tan, currentLogoAttachmentId]);

  useEffect(() => {
    const logoUrlFromSettings = getSetting('fpo_logo_url');
    if (logoUrlFromSettings && !logoPreview) {
      setLogoPreview(logoUrlFromSettings);
      return;
    }

    const urlFromHook = (logoUrlData as any)?.data?.download_url || (logoUrlData as any)?.data?.url;
    if (urlFromHook && !logoPreview) {
      setLogoPreview(urlFromHook);
    }
    
    if (logoUrlError && logoAttachmentId) {
      console.warn(`Attachment ${logoAttachmentId} not found - it may have been deleted`);
    }
  }, [logoUrlData, logoUrlError, logoAttachmentId, getSetting, logoPreview]);

  const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setLogoError('Invalid file type. Please upload PNG, JPG, JPEG, or GIF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setLogoError('File size must be less than 10MB');
      return;
    }

    setLogoError(null);
    setLogoFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const createSaveHandler = (key: string, value: string, schema?: any) => {
    return async () => {
      if (schema) {
        try {
          schema.parse(value);
        } catch (err) {
          setSaveStatus({ ...saveStatus, [key]: 'error' });
          if (err && typeof err === 'object' && 'errors' in err) {
            const zodError = err as { errors: Array<{ message: string }> };
            setErrors({ ...errors, [key]: zodError.errors[0]?.message || `Invalid ${key}` });
          } else {
            setErrors({ ...errors, [key]: `Invalid ${key}` });
          }
          return;
        }
      }

      setSaveStatus({ ...saveStatus, [key]: 'saving' });
      setErrors({ ...errors, [key]: '' });

      try {
        await upsertSetting.mutateAsync({ key: `fpo_${key}`, value: value.trim() });
        await forceRefetchSettings();
        setSaveStatus({ ...saveStatus, [key]: 'success' });
        setTimeout(() => setSaveStatus({ ...saveStatus, [key]: 'idle' }), 2000);
      } catch (err) {
        setSaveStatus({ ...saveStatus, [key]: 'error' });
        setErrors({ ...errors, [key]: err instanceof Error ? err.message : `Failed to save ${key}` });
      }
    };
  };

  const handleSaveFpoName = createSaveHandler('name', fpoName);
  const handleSaveGstin = createSaveHandler('gstin', gstin, gstinSchema);
  const handleSavePan = createSaveHandler('pan', pan, panSchema);
  const handleSaveCin = createSaveHandler('cin', cin, cin.trim() ? cinSchema : undefined);
  const handleSaveTan = createSaveHandler('tan', tan, tan.trim() ? tanSchema : undefined);

  const handleUploadLogo = async () => {
    if (!logoFile) {
      setLogoError('Please select a logo file');
      return;
    }

    setSaveStatus({ ...saveStatus, logo: 'saving' });
    setLogoError(null);

    try {
      const uploadResult = await uploadLogo.mutateAsync({
        entityType: 'logo',
        entityId: 'fpo',
        file: logoFile,
      });

      let uploadData: { id: string; download_url?: string } | null = (uploadResult as any)?.data?.data || (uploadResult as any)?.data || uploadResult;
      
      if (!uploadData?.id) {
        console.error('[FPOSettingsPage] Upload response structure:', uploadResult);
        throw new Error('Failed to get attachment ID from upload.');
      }

      setCurrentLogoAttachmentId(uploadData.id);

      let logoUrl = uploadData.download_url;
      if (!logoUrl && uploadData.id) {
        try {
          const urlResponse = await (erpClient as any).attachments.getDownloadUrl(uploadData.id);
          logoUrl = urlResponse?.data?.download_url || urlResponse?.data?.url || urlResponse?.download_url || urlResponse?.url || (typeof urlResponse?.data === 'string' ? urlResponse.data : undefined);
        } catch (urlError) {
          console.warn('[FPOSettingsPage] Failed to fetch download URL:', urlError);
        }
      }

      if (logoUrl) setLogoPreview(logoUrl);
      else setLogoPreview(null);

      await upsertSetting.mutateAsync({ key: 'fpo_logo_attachment_id', value: uploadData.id });
      if (logoUrl) {
        await upsertSetting.mutateAsync({ key: 'fpo_logo_url', value: logoUrl });
      }

      await forceRefetchSettings();
      setSaveStatus({ ...saveStatus, logo: 'success' });
      setTimeout(() => setSaveStatus({ ...saveStatus, logo: 'idle' }), 2000);
    } catch (err) {
      setSaveStatus({ ...saveStatus, logo: 'error' });
      setLogoError(err instanceof Error ? err.message : 'Failed to upload logo');
    }
  };

  const createAddressSaveHandler = (addressType: 'branch' | 'registered', address: Address) => {
    return async () => {
      try {
        const validated = addressSchema.parse(address);
        const jsonValue = stringifyAddress(validated);

        setSaveStatus({ ...saveStatus, [`${addressType}Address`]: 'saving' });
        setErrors({ ...errors, [`${addressType}Address`]: '' });

        await upsertSetting.mutateAsync({ key: `fpo_${addressType}_address`, value: jsonValue });
        await forceRefetchSettings();
        
        setSaveStatus({ ...saveStatus, [`${addressType}Address`]: 'success' });
        setTimeout(() => setSaveStatus({ ...saveStatus, [`${addressType}Address`]: 'idle' }), 2000);
      } catch (err) {
        setSaveStatus({ ...saveStatus, [`${addressType}Address`]: 'error' });
        if (err && typeof err === 'object' && 'errors' in err) {
          const zodError = err as { errors: Array<{ message: string }> };
          setErrors({ ...errors, [`${addressType}Address`]: zodError.errors[0]?.message });
        } else {
          setErrors({ ...errors, [`${addressType}Address`]: err instanceof Error ? err.message : 'Failed to save address' });
        }
      }
    };
  };

  const handleSaveBranchAddress = createAddressSaveHandler('branch', branchAddress);
  const handleSaveRegisteredAddress = createAddressSaveHandler('registered', registeredAddress);

  const handleSaveBankDetails = async () => {
    try {
      const validated = bankDetailsSchema.parse(bankDetails);
      const jsonValue = stringifyBankDetails(validated);

      setSaveStatus({ ...saveStatus, bankDetails: 'saving' });
      setErrors({ ...errors, bankDetails: '' });

      await upsertSetting.mutateAsync({ key: 'fpo_bank_details', value: jsonValue });
      await forceRefetchSettings();

      setSaveStatus({ ...saveStatus, bankDetails: 'success' });
      setTimeout(() => setSaveStatus({ ...saveStatus, bankDetails: 'idle' }), 2000);
    } catch (err) {
      setSaveStatus({ ...saveStatus, bankDetails: 'error' });
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ message: string }> };
        setErrors({ ...errors, bankDetails: zodError.errors[0]?.message });
      } else {
        setErrors({ ...errors, bankDetails: err instanceof Error ? err.message : 'Failed to save bank details' });
      }
    }
  };

  const updateAddressField = (setter: React.Dispatch<React.SetStateAction<Address>>, field: keyof Address, value: string) => {
    setter(prev => ({ ...prev, [field]: value }));
  };

  const updateBankField = (field: keyof BankDetails, value: string) => {
    setBankDetails(prev => ({ ...prev, [field]: value }));
  };

  // Handlers used by JSX sections (to be wired up)
  void logoError; void handleLogoFileSelect;
  void handleSaveFpoName; void handleSaveGstin; void handleSavePan;
  void handleSaveCin; void handleSaveTan; void handleUploadLogo;
  void handleSaveBranchAddress; void handleSaveRegisteredAddress;
  void handleSaveBankDetails; void updateAddressField; void updateBankField;

  if (isLoadingSettings) {
    return <div className="text-center p-8"><Icon name="Loader" size={48} className="animate-spin" /></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">FPO Settings</h1>
          <p className="text-sm text-neutral-600 mt-1">
            Configure your company information, logo, addresses, and bank details for invoice generation
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isCached && (
            <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded border border-neutral-200">
              Using cached data
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={forceRefetchSettings} className="border border-neutral-200">
            <Icon name="RefreshCw" size={14} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Sections for Logo, Company Name, Compliance, Addresses, Bank Details */}
        {/* ... existing JSX for sections ... */}
      </div>
    </div>
  );
}

